import {
  Chart as ChartJS,
  LineController,
  BarController,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Filler,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowDownRight,
  ArrowUpRight,
  CalendarDays,
  Minus,
  Plus,
  TrendingUp,
} from "lucide-react";
import styles from "./MonthlyKpi.module.css";
import Meter from "../Meter/Meter";
import { fmtEUR, fmtInt } from "../../../utils/moneyFormat";

ChartJS.register(
  LineController,
  BarController,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Filler
);

// Canvas cannot read CSS custom properties, so the chart mirrors the design
// tokens from styles/dashboard.css as literals. Keep these in sync by hand.
const ACCENT = "#2563eb";
const ACCENT_LIGHT = "#60a5fa";
const GRID = "#e6eaf1";
const TICK = "#a9b1bd";
const TOOLTIP_BG = "#0b1220";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// A dashed rule dropped through the hovered day, so the eye can carry a point
// on the line down to its date without the tooltip having to say so.
const crosshair = {
  id: "sunskyCrosshair",
  afterDatasetsDraw(chart) {
    const active = chart.tooltip?.getActiveElements?.() || [];
    if (!active.length) return;
    const { ctx, chartArea } = chart;
    const { x } = active[0].element;
    ctx.save();
    ctx.beginPath();
    ctx.setLineDash([3, 4]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(37,99,235,0.42)";
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.stroke();
    ctx.restore();
  },
};

// The running total's head. A haloed dot marks where the month currently
// stands, which is the one point on the line anybody looks for.
const headMarker = {
  id: "sunskyHeadMarker",
  afterDatasetsDraw(chart) {
    const meta = chart.getDatasetMeta(0);
    const pt = meta?.data?.[meta.data.length - 1];
    if (!pt || !Number.isFinite(pt.x)) return;
    const { ctx } = chart;
    ctx.save();
    ctx.beginPath();
    ctx.arc(pt.x, pt.y, 8, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(37,99,235,0.14)";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(pt.x, pt.y, 3.6, 0, Math.PI * 2);
    ctx.fillStyle = ACCENT;
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#ffffff";
    ctx.stroke();
    ctx.restore();
  },
};

// Placeholder chart for a month that has not produced yet: a dashed baseline
// under a faint, drifting curve. It says "this is where revenue will appear"
// without inventing figures.
const GhostChart = () => (
  <svg
    className={styles.ghost}
    viewBox="0 0 320 120"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="ghostFill" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#94a3b8" stopOpacity="0.16" />
        <stop offset="100%" stopColor="#94a3b8" stopOpacity="0" />
      </linearGradient>
    </defs>
    {[24, 54, 84].map((y) => (
      <line
        key={y}
        x1="0"
        y1={y}
        x2="320"
        y2={y}
        stroke={GRID}
        strokeWidth="1"
        strokeDasharray="3 5"
      />
    ))}
    <g className={styles.ghostCurve}>
      <path
        d="M0,104 C40,100 62,74 96,70 C132,66 148,86 184,74 C222,62 240,30 276,26 C296,24 310,20 320,18 L320,120 L0,120 Z"
        fill="url(#ghostFill)"
      />
      <path
        d="M0,104 C40,100 62,74 96,70 C132,66 148,86 184,74 C222,62 240,30 276,26 C296,24 310,20 320,18"
        fill="none"
        stroke="#b6c0cf"
        strokeWidth="1.5"
        strokeDasharray="5 6"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </g>
    <line x1="0" y1="119" x2="320" y2="119" stroke={GRID} strokeWidth="1.5" />
  </svg>
);

// Revenue over the selected period: cumulative line for the trajectory, daily
// bars for the texture underneath it — where the money actually landed, day by
// day. Long periods arrive bucketed by month; only the labels differ.
const MonthlySalesKPI = ({ series, revenue, period, loading, delta }) => {
  const navigate = useNavigate();
  const points = useMemo(() => series?.points || [], [series]);

  const periodTotal = Number(series?.total) || 0;
  const unit = series?.unit || 'Day';
  const elapsed = Math.max(0, Number(series?.elapsed) || 0);
  const span = Math.max(1, Number(series?.span) || 1);
  const elapsedPct = Math.min(100, Math.round((elapsed / span) * 100));
  const left = Math.max(0, span - elapsed);
  const unitWord = (n) => `${unit.toLowerCase()}${n === 1 ? '' : 's'}`;
  // Points can exist with nothing in them — the first days of a month always
  // do. What decides the empty state is whether any money has landed.
  const isEmpty = !loading && periodTotal <= 0;

  const stats = useMemo(() => {
    let best = null;
    points.forEach((p) => {
      const rev = Number(p?.revenue) || 0;
      if (rev > 0 && (!best || rev > best.revenue)) {
        best = { label: p.label, revenue: rev };
      }
    });
    const total = Number(series?.total) || 0;
    const buckets = Math.max(1, Number(series?.elapsed) || 1);
    const bookings = Number(series?.bookings) || 0;
    return {
      best,
      dailyAverage: total / buckets,
      perBooking: bookings ? total / bookings : 0,
    };
  }, [points, series]);

  const maxDaily = useMemo(
    () => points.reduce((m, p) => Math.max(m, Number(p?.revenue) || 0), 0),
    [points]
  );

  const chartData = useMemo(
    () => ({
      labels: points.map((p) => p.label),
      datasets: [
        {
          type: "line",
          data: points.map((p) => p.cumulative),
          borderWidth: 2.5,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHoverBorderWidth: 2,
          pointHoverBorderColor: "#ffffff",
          pointHoverBackgroundColor: ACCENT,
          pointHitRadius: 14,
          tension: 0.35,
          fill: true,
          yAxisID: "y",
          order: 0,
          // The line brightens as it travels, so the head of the month reads
          // as the live end of the series.
          borderColor: (ctx) => {
            const { chart } = ctx;
            if (!chart.chartArea) return ACCENT;
            const g = chart.ctx.createLinearGradient(
              chart.chartArea.left,
              0,
              chart.chartArea.right,
              0
            );
            g.addColorStop(0, "#1e40af");
            g.addColorStop(1, ACCENT_LIGHT);
            return g;
          },
          backgroundColor: (ctx) => {
            const { chart } = ctx;
            if (!chart.chartArea) return "rgba(37,99,235,0.12)";
            const g = chart.ctx.createLinearGradient(
              0,
              chart.chartArea.top,
              0,
              chart.chartArea.bottom
            );
            g.addColorStop(0, "rgba(37,99,235,0.26)");
            g.addColorStop(0.55, "rgba(37,99,235,0.09)");
            g.addColorStop(1, "rgba(37,99,235,0)");
            return g;
          },
        },
        {
          type: "bar",
          data: points.map((p) => p.revenue),
          borderRadius: 4,
          borderSkipped: false,
          maxBarThickness: 14,
          yAxisID: "y1",
          order: 1,
          // The bars only ever occupy the bottom third of the plot, so the
          // gradient is laid over that band — spanning the full height would
          // leave every bar in the palest part of the ramp.
          backgroundColor: (ctx) => {
            const { chart } = ctx;
            if (!chart.chartArea) return "rgba(245,165,30,0.72)";
            const { top, bottom } = chart.chartArea;
            const g = chart.ctx.createLinearGradient(
              0,
              bottom - (bottom - top) / 3,
              0,
              bottom
            );
            g.addColorStop(0, "rgba(240,155,15,0.95)");
            g.addColorStop(1, "rgba(245,165,30,0.42)");
            return g;
          },
          hoverBackgroundColor: "#f5a51e",
        },
      ],
    }),
    [points]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      animation: prefersReducedMotion()
        ? false
        : { duration: 1100, easing: "easeOutQuart" },
      layout: { padding: { top: 10, right: 8 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: TOOLTIP_BG,
          titleColor: "#ffffff",
          bodyColor: "#c7cedb",
          titleFont: { size: 12, weight: "600" },
          bodyFont: { size: 11.5 },
          titleMarginBottom: 6,
          padding: 11,
          cornerRadius: 10,
          displayColors: false,
          caretSize: 5,
          // Both datasets share one x-index; a single 3-line entry covers the
          // pair, so the bar dataset is filtered out to avoid duplicates.
          filter: (item) => item.datasetIndex === 0,
          callbacks: {
            title: (items) => points[items[0]?.dataIndex]?.label || "",
            label: (ctx) => {
              const p = points[ctx.dataIndex];
              if (!p) return "";
              return [
                `Running total  ${fmtEUR(p.cumulative)}`,
                `This day  ${fmtEUR(p.revenue)}`,
                `Bookings  ${fmtInt(p.bookings)}`,
              ];
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false, drawTicks: false },
          border: { display: false },
          ticks: {
            color: TICK,
            font: { size: 10.5 },
            padding: 6,
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 7,
          },
        },
        y: {
          beginAtZero: true,
          grid: { color: GRID, drawTicks: false, lineWidth: 1 },
          border: { display: false, dash: [3, 4] },
          ticks: {
            color: TICK,
            font: { size: 10.5 },
            padding: 10,
            maxTicksLimit: 5,
            // Axis labels abbreviate, but the grouping still comes from the
            // shared formatter so separators match the rest of the dashboard.
            callback: (v) =>
              Math.abs(v) >= 1000 ? `€ ${fmtInt(v / 1000)}k` : `€ ${fmtInt(v)}`,
          },
        },
        // Hidden scale for the daily bars, capped well above the best day so
        // the bars stay a low skyline under the line rather than competing.
        y1: {
          display: false,
          beginAtZero: true,
          suggestedMax: maxDaily * 3.1 || 1,
        },
      },
    }),
    [points, maxDaily]
  );

  const prevLabel = period?.previous?.label || "the previous period";

  const renderDelta = () => {
    if (delta === null || delta === undefined) return null;
    // A period that has not produced yet reads as quiet, not as a collapse —
    // the same neutral dash the KPI strip uses, never "-100%".
    if (delta === 0 || periodTotal <= 0) {
      return (
        <span className="dshPill dshPillMute" title="No change to report yet">
          <Minus size={10} strokeWidth={3} />
        </span>
      );
    }
    const up = delta > 0;
    const Arrow = up ? ArrowUpRight : ArrowDownRight;
    return (
      <span
        className={`dshPill ${up ? "dshPillPos" : "dshPillNeg"}`}
        title={`${up ? "Up" : "Down"} ${Math.abs(delta)}% vs ${prevLabel}${
          period?.isToDate ? " to the same point" : ""
        }`}
      >
        <Arrow size={10} strokeWidth={3} />
        {Math.abs(delta)}%
      </span>
    );
  };

  // With no revenue to describe, average / per booking / best day are three
  // zeros. Standing figures that do exist are shown instead.
  const statCells = isEmpty
    ? [
        { label: "Year to date", value: fmtEUR(revenue?.year ?? 0) },
        {
          label: prevLabel,
          value:
            revenue?.prevPeriod === undefined ? "—" : fmtEUR(revenue.prevPeriod),
        },
        {
          label: `${unit}s remaining`,
          value: period?.isFuture ? "—" : fmtInt(left),
        },
      ]
    : [
        {
          label: unit === "Month" ? "Monthly average" : "Daily average",
          value: fmtEUR(stats.dailyAverage),
        },
        { label: "Per booking", value: fmtEUR(stats.perBooking) },
        {
          label: `Best ${unit.toLowerCase()}`,
          value: stats.best ? fmtEUR(stats.best.revenue) : "—",
          when: stats.best?.label,
        },
      ];

  return (
    <div className={`dshPanel ${styles.root}`}>
      <div className="dshHead">
        <div className={styles.headText}>
          <h3 className="dshTitle">Revenue</h3>
          <p className="dshSub">
            {series?.label || "This month"}
            {period?.isToDate ? " · to date" : ""}
          </p>
        </div>
        <span className="dshPill dshPillAccent dshNum">
          <CalendarDays size={12} strokeWidth={2.2} />
          {unit} {fmtInt(elapsed)} of {fmtInt(span)}
        </span>
      </div>

      {/* How far through the period we are — the denominator behind every
          figure in this panel, drawn edge to edge. */}
      <Meter
        pct={elapsedPct}
        tone="brand"
        size="xs"
        className={styles.monthRail}
        label={`${elapsedPct}% of ${series?.label || "the period"} elapsed`}
      />

      <div className={styles.hero}>
        {loading ? (
          <div className={styles.heroMain}>
            <span className={`dshSkel ${styles.skelFigure}`} />
            <span className={`dshSkel ${styles.skelMeta}`} />
          </div>
        ) : (
          <>
            <div className={styles.heroMain}>
              <span className={styles.figureRow}>
                <span className={`${styles.figure} dshNum`}>
                  {fmtEUR(periodTotal)}
                </span>
                {renderDelta()}
              </span>
              <span className={styles.meta}>
                from{" "}
                <span className="dshNum">{fmtInt(series?.bookings ?? 0)}</span>{" "}
                bookings
              </span>
            </div>

            <div className={styles.stats}>
              {statCells.map((s) => (
                <div className={styles.stat} key={s.label}>
                  <span className="dshEyebrow">{s.label}</span>
                  <span className={`${styles.statValue} dshNum`}>
                    {s.when && <em className={styles.statWhen}>{s.when}</em>}
                    {s.value}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className={styles.chartWrap}>
        {loading ? (
          <span className={`dshSkel ${styles.skelChart}`} />
        ) : isEmpty ? (
          <div className={styles.blank}>
            <GhostChart />
            <div className={styles.blankCard}>
              <span className="dshBlankIcon">
                <TrendingUp size={19} strokeWidth={2.1} />
              </span>
              <p className="dshBlankTitle">
                {period?.isFuture
                  ? `${series?.label || "This period"} has not started yet`
                  : `Nothing booked in ${series?.label || "this period"}`}
              </p>
              <p className="dshBlankText">
                {period?.isFuture
                  ? "Bookings will appear here once the period opens."
                  : left > 0
                  ? `${elapsedPct}% of the period has passed, ${fmtInt(left)} ${unitWord(
                      left
                    )} still to run.`
                  : "The period closed without revenue."}
                {revenue?.prevPeriod > 0 &&
                  ` ${prevLabel} stood at ${fmtEUR(revenue.prevPeriod)}${
                    period?.isToDate ? " by this point" : ""
                  }.`}
              </p>
              <div className={styles.blankActions}>
                <button
                  type="button"
                  className={styles.blankPrimary}
                  onClick={() => navigate("/bookings/add")}
                >
                  <Plus size={14} strokeWidth={2.6} />
                  New booking
                </button>
                <button
                  type="button"
                  className="dshAction"
                  onClick={() => navigate("/bookings")}
                >
                  View all bookings
                </button>
              </div>
            </div>
          </div>
        ) : (
          <Chart
            type="bar"
            data={chartData}
            options={options}
            plugins={[crosshair, headMarker]}
          />
        )}
      </div>

      {!loading && !isEmpty && (
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <i className={styles.keyLine} />
            Running total
          </span>
          <span className={styles.legendItem}>
            <i className={styles.keyBar} />
            Daily revenue
          </span>
        </div>
      )}
    </div>
  );
};

export default MonthlySalesKPI;
